import { scaleLinear } from 'd3-scale';
import styled from 'styled-components';
import {
  defaultValuesAlignmentValueSets,
  friendlyAlignmentLevel,
  friendlyValuesAlignmentValueSetName,
  getNetImpactRatioInValueSet,
  toAlignmentLevel,
} from '../../../../../../../common/model/input/value-sets/alignment-level';
import { ValueSetId } from '../../../../../../../common/model/input/value-sets/types';
import { BouColor } from '../../../../../generic/global/bou-colors';
import { BouSpacingLevel, fluidSpacing } from '../../../../../generic/layout/bou-spacing';
import { ComparisonItem } from '../comparison/types';

const colors = ['#9819E3', 'rgba(0,255,0,0.0)', '#00B998'];
const colorScale = scaleLinear<number, string>()
  .domain([-10, 0, 2])
  .range(colors as unknown as number[]);

interface Props {
  items: ComparisonItem[];
  valueSetIds?: ValueSetId[];
}

export function ValuesAlignmentTable(props: Props) {
  const allItems = props.items;
  const items = allItems.slice(0, 6);

  const referenceValueSetIds = props.valueSetIds || defaultValuesAlignmentValueSets;

  const header = items.map((item, index) => {
    const first = index === 0;
    const name = item.name;
    return (
      <RotatedHeader key={index}>
        <RotatedHeaderWrap>
          <RotatedHeaderInner style={{ fontWeight: first ? 'bold' : undefined }}>{name}</RotatedHeaderInner>
        </RotatedHeaderWrap>
      </RotatedHeader>
    );
  });

  const rows = referenceValueSetIds.map((valueSet, valueSetIndex) => {
    const alignment = toAlignmentLevel(getNetImpactRatioInValueSet(items[0]?.result, valueSet)!);
    return (
      <Row key={valueSet}>
        <ValueSetTitle>{friendlyValuesAlignmentValueSetName(valueSet)}</ValueSetTitle>
        <AlignmentCell>{valueSet !== ValueSetId.EQUAL_WEIGHTS ? friendlyAlignmentLevel(alignment) : ''}</AlignmentCell>
        {items.map((item, index) => {
          const first = index === 0;
          const netImpactRatio = getNetImpactRatioInValueSet(item.result, valueSet);
          if (netImpactRatio === undefined) {
            return <NumberCell key={index}>&nbsp;</NumberCell>;
          }
          const color = colorScale(netImpactRatio);
          return (
            <NumberCell
              key={index}
              style={{
                backgroundColor: color,
                fontWeight: first ? 'bold' : undefined,
                borderBottom:
                  valueSetIndex === referenceValueSetIds.length - 1
                    ? `1px solid ${BouColor.TOOL_BORDER_DARK_GRAY}`
                    : undefined,
              }}
            >
              {(netImpactRatio * 100).toFixed(0)}%
            </NumberCell>
          );
        })}
      </Row>
    );
  });

  return (
    <Root>
      <Table>
        <thead>
          <TitleRow>
            <ValueSetsTitle>Value lens</ValueSetsTitle>
            <AlignmentTitle>Alignment of {items[0]?.name}</AlignmentTitle>
            {header}
          </TitleRow>
        </thead>
        <tbody>
          {rows}
          <Row>
            <td />
            <td colSpan={items.length + 1}>
              <Legend>
                <LegendText>Poorly aligned</LegendText>
                <Gradient />
                <LegendText>Well aligned</LegendText>
              </Legend>
            </td>
          </Row>
          <Row>
            <td />
            <td />
            <RatioExplanation colSpan={items.length}>
              Net impact ratio in stakeholder group's value set
            </RatioExplanation>
          </Row>
        </tbody>
      </Table>
    </Root>
  );
}

const Root = styled.div`
  overflow: visible;
`;

const TitleRow = styled.tr`
  th td {
    border-bottom: 1px solid ${BouColor.TOOL_BORDER_DARK_GRAY};
  }
`;

const RatioExplanation = styled.td`
  text-align: center;
  font-style: italic;
  font-weight: bold;
  ${fluidSpacing('padding-top', BouSpacingLevel.TINY)};
`;

const Table = styled.table`
  border-collapse: collapse;
  th {
    height: 120px;
    vertical-align: bottom;
    text-align: left;
    line-height: 1;
  }
`;

const Row = styled.tr``;
const ValueSetTitle = styled.td`
  ${fluidSpacing('padding-right', BouSpacingLevel.SMALL)}
  border-bottom: 1px solid rgb(238, 238, 238);
  width: 240px;
`;

const NumberCell = styled.td`
  border: 1px solid rgb(238, 238, 238);
  border: none;
  text-align: center;
  width: 50px;
  height: 30px;
`;

const AlignmentCell = styled.td`
  border-top: 1px solid rgb(238, 238, 238);
  border-bottom: 1px solid rgb(238, 238, 238);
  text-align: left;
  height: 40px;
  ${fluidSpacing('padding-left', BouSpacingLevel.TINYISH)}
`;

const AlignmentTitle = styled.th`
  font-style: italic;
  ${fluidSpacing('padding-bottom', BouSpacingLevel.TINY)}
  border-bottom: 1px solid ${BouColor.TOOL_BORDER_BLACK};
  ${fluidSpacing('padding-left', BouSpacingLevel.TINYISH)}
  ${fluidSpacing('padding-right', BouSpacingLevel.TINY)}
`;

const ValueSetsTitle = styled.th`
  font-style: italic;
  ${fluidSpacing('padding-bottom', BouSpacingLevel.TINY)}
  border-bottom: 1px solid ${BouColor.TOOL_BORDER_BLACK};
`;

const RotatedHeader = styled.th`
  text-align: right;
  border-bottom: 1px solid ${BouColor.TOOL_BORDER_BLACK};
`;

const RotatedHeaderWrap = styled.div`
  width: 40px;
`;

const RotatedHeaderInner = styled.div`
  width: 140px;
  transform-origin: bottom left;
  ${fluidSpacing('padding-left', BouSpacingLevel.TINY)};
  transform: translateX(20px) rotate(-45deg);
`;

const Legend = styled.div`
  margin-top: 1em;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  > * + * {
    margin-left: 1em;
  }
  width: 100%;
  font-style: italic;
  font-size: 0.8em;
`;
const LegendText = styled.div``;

const Gradient = styled.div`
  width: 10em;
  height: 0.5em;
  background: linear-gradient(90deg, #bb77e2, #ffffff, #68c5b4);
  border-radius: 0.3em;
`;
